import React, { useState, ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader } from "../core/card";
import { ChevronDownIcon, ChevronUpIcon, GripVerticalIcon, TrashIcon } from "lucide-react";
import { Button } from "../core/button";
import { Badge } from "../core/badge";

export interface SortableItemData {
  id?: string;
  name: string;
  description?: string;
  type?: string;
  [key: string]: any; // Allow for additional properties
}

interface ExpandableSortableItemProps<T extends SortableItemData> {
  item: T;
  onUpdate: (item: T) => void;
  onDelete?: () => void;
  // onUndo: () => void; // TODO: Move this back to child component
  renderContent: (item: T, onUpdate: (item: T) => void) => ReactNode;
  defaultExpanded?: boolean;
  deleted?: boolean;
  renderActions?: (item: T) => ReactNode;
}

export function ExpandableSortableItem<T extends SortableItemData>({
  item,
  onUpdate,
  onDelete,
  renderContent,
  defaultExpanded = false,
  deleted = false,
  renderActions,
}: ExpandableSortableItemProps<T>) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Set up sortable functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      className="mb-3 border shadow-sm"
    >
      <CardHeader className="p-3 flex flex-row items-center justify-between bg-gray-50 border-b">
        <div className="flex items-center flex-1">
          <div 
            {...attributes} 
            {...listeners} 
            className="mr-3 cursor-grab touch-none p-1 hover:bg-gray-100 rounded"
          >
            <GripVerticalIcon className="h-5 w-5 text-gray-500" />
          </div>
          <div className="flex flex-col">
            <div className="font-medium truncate">{item.name}</div>
            <div className="flex items-center mt-1">
              {item.type && (
                <Badge variant="outline" className="text-xs">
                  {item.type}
                </Badge>
              )}
              {item.description && (
                <span className="text-xs text-gray-500 ml-2 truncate max-w-md">
                  {item.description}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-1 items-center">
          {renderActions ? (
            <div className="flex items-center">{renderActions(item)}</div>
          ) : null}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleExpand}
            className="p-1 h-8 w-8"
          >
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </Button>
          {!deleted && onDelete ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="p-1 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="h-5 w-5" />
            </Button>
          ) : null}
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="p-4 bg-white">
          {renderContent(item, onUpdate)}
        </CardContent>
      )}
    </Card>
  );
}